import React, { FC, useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomInput from './subComponents/CustomInput';
import { Icons } from '@/icons';

import AuthService from '@/services/AuthService';
import { toast } from 'react-toastify';

const { Name, Email, Phone, DOB, Lock, EyeOpen, EyeClose } = Icons;

const CreateAccountForm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = (location.state as { email?: string })?.email;

  useEffect(() => {
    if (userEmail) {
      setEmail(userEmail); // pre-fill invite email
    }
  }, [userEmail]);

  // form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [gender, setGender] = useState('');

  // errors
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // show/hide passwords
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // validation functions
  const validateName = (value: string) => {
    const ok = value.trim().length > 0;
    setNameError(!ok);
    return ok;
  };

  const validateEmail = (value: string) => {
    const ok = /.+@.+\..+/.test(value);
    setEmailError(!ok);
    return ok;
  };

  const validatePhone = (value: string) => {
    if (!value) {
      setPhoneError(false);
      return true;
    }
    const ok = /^\d{10,15}$/.test(value);
    setPhoneError(!ok);
    return ok;
  };

  const validateDob = (value: string) => {
    const ok = !!value && new Date(value) < new Date();
    setDobError(!ok);
    return ok;
  };

  const validatePassword = (value: string) => {
    const ok = value.length >= 6;
    setPasswordError(!ok);
    return ok;
  };

  const validateConfirm = (value: string) => {
    const ok = value === password;
    setConfirmError(!ok);
    return ok;
  };

  const isFormValid =
    !nameError &&
    !emailError &&
    !phoneError &&
    !dobError &&
    !passwordError &&
    !confirmError &&
    !!name.trim() &&
    !!email.trim() &&
    !!dateOfBirth &&
    !!password &&
    !!confirmPassword &&
    !!gender &&
    termsAccepted;

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isFormValid) return;

    const result = await AuthService.register(
      name,
      email,
      phone,
      dateOfBirth,
      password,
      gender,
    );

    if (result.success) {
      toast.success(`${result.message}`);
      // ✅ navigate to verify page with link in state
      navigate('/welcome', { state: { verifyLink: result.verifyLink, email } });
    } else {
      toast.error(result.message || 'Failed to register');
    }
  };

  const borderColor = !gender ? 'border-[#FF0000]' : 'border-gray-300';

  return (
    <form className='flex w-full flex-col items-center' noValidate>
      {/* Name */}
      <CustomInput
        id='name'
        label='Name'
        Icon={Name}
        type='text'
        placeholder='Enter full name here'
        value={name}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setName(e.target.value);
          if (nameError) validateName(e.target.value);
        }}
        error={nameError}
        errorMessage='Name is required'
      />

      {/* Email */}
      <CustomInput
        id='email'
        label='Email'
        Icon={Email}
        type='email'
        placeholder='Enter email here'
        value={email}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (!userEmail) {
            // ✅ block editing if userEmail is pre-filled
            setEmail(e.target.value);
            if (emailError) validateEmail(e.target.value);
          }
        }}
        error={emailError}
        errorMessage='Enter valid email'
        // ✅ make read-only if userEmail exists
        readOnly={!!userEmail}
      />

      {/* Phone (optional) */}
      <CustomInput
        id='phone'
        label='Phone Number (Optional)'
        Icon={Phone}
        type='tel'
        placeholder='Enter number here'
        value={phone}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setPhone(e.target.value);
          if (phoneError) validatePhone(e.target.value);
        }}
        error={phoneError}
        errorMessage='Enter valid number (10–15 digits)'
      />

      {/* DOB */}
      <CustomInput
        id='dob'
        label='Date of Birth'
        Icon={DOB}
        type='date'
        value={dateOfBirth}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setDob(e.target.value);
          if (dobError) validateDob(e.target.value);
        }}
        error={dobError}
        errorMessage='Date of birth is required'
      />

      {/* Gender (bullet options) */}
      <div
        className={`w-full bg-white h-16 relative mb-2 mt-0 border-b-2 pt-1 px-3 ${borderColor}`}
      >
        <label className='block text-gray-600 mb-1'>Gender</label>
        <div className='flex flex-row space-x-10'>
          {[
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' },
            { label: 'Other', value: 'other' },
          ].map((g) => (
            <label key={g.value} className='flex items-center space-x-2'>
              <input
                type='radio'
                name='gender'
                value={g.value}
                checked={gender === g.value}
                onChange={(e) => setGender(e.target.value)}
              />
              <span>{g.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Password */}
      <CustomInput
        id='password'
        label='Password'
        Icon={Lock}
        type={showPassword ? 'text' : 'password'}
        placeholder='Enter password here'
        value={password}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setPassword(e.target.value);
          if (passwordError) validatePassword(e.target.value);
        }}
        RightIcon={showPassword ? EyeOpen : EyeClose}
        onRightIconClick={() => setShowPassword((v) => !v)}
        error={passwordError}
        errorMessage='Password must be at least 6 characters'
      />

      {/* Confirm Password */}
      <CustomInput
        id='confirmPassword'
        label='Confirm Password'
        Icon={Lock}
        type={showConfirm ? 'text' : 'password'}
        placeholder='Re-enter password here'
        value={confirmPassword}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setConfirmPassword(e.target.value);
          if (confirmError) validateConfirm(e.target.value);
        }}
        password={password}
        RightIcon={showConfirm ? EyeOpen : EyeClose}
        onRightIconClick={() => setShowConfirm((v) => !v)}
        error={confirmError}
        errorMessage='Passwords must match'
      />

      {/* Submit */}
      <button
        type='button'
        onClick={handleSubmit}
        disabled={!isFormValid}
        className={`w-full text-white py-2 rounded-full mt-6 transition ${
          isFormValid
            ? 'bg-[#FF0050] hover:bg-[#14D8CE]'
            : 'bg-gray-400 cursor-not-allowed'
        }`}
      >
        Create Account
      </button>

      {/* Terms */}
      <div className='w-full flex items-center mt-4 ml-8'>
        <input
          id='terms'
          type='checkbox'
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className='h-4 w-4 text-pink-500 border-gray-300 rounded'
        />
        <label htmlFor='terms' className='ml-2 text-sm'>
          Agree with{' '}
          <a
            href='https://www.funizzle.com/terms'
            target='_blank'
            rel='noopener noreferrer'
            className='text-[#14D8CE] hover:underline'
          >
            Terms &amp; Conditions
          </a>{' '}
          and{' '}
          <a
            href='https://www.funizzle.com/privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
            className='text-[#14D8CE] hover:underline'
          >
            Privacy Policy
          </a>
        </label>
      </div>
    </form>
  );
};

export default CreateAccountForm;
